import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { z } from "zod";

/* eslint-disable react-refresh/only-export-components -- the schema, bootstrap helper, and hook share this provider's context contract */

export const ThemeSchema = z.enum(["light", "dark", "system"]);

export type Theme = z.infer<typeof ThemeSchema>;
export type ResolvedTheme = Exclude<Theme, "system">;

export const THEME_STORAGE_KEY = "das-theme";

const DEFAULT_THEME: Theme = "system";
const DARK_MODE_QUERY = "(prefers-color-scheme: dark)";
const THEME_COLORS = {
  light: "#FAFBFC",
  dark: "#0B1117",
} as const satisfies Record<ResolvedTheme, string>;

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return DEFAULT_THEME;

  try {
    const storedTheme = ThemeSchema.safeParse(window.localStorage.getItem(THEME_STORAGE_KEY));
    return storedTheme.success ? storedTheme.data : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // A blocked storage backend must not prevent an in-memory theme change.
  }
}

function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(theme);
  root.style.colorScheme = theme;
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[theme]);
}

export function initializeTheme(): Theme {
  const theme = readStoredTheme();
  const resolvedTheme =
    theme === "system" ? (window.matchMedia(DARK_MODE_QUERY).matches ? "dark" : "light") : theme;
  applyTheme(resolvedTheme);
  return theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  const setTheme = useCallback((nextTheme: Theme) => {
    persistTheme(nextTheme);
    setThemeState(nextTheme);
  }, []);

  useLayoutEffect(() => {
    if (theme !== "system") {
      applyTheme(theme);
      return;
    }

    const preference = window.matchMedia(DARK_MODE_QUERY);
    const applySystemTheme = (prefersDark: boolean) => {
      applyTheme(prefersDark ? "dark" : "light");
    };
    const handleChange = (event: MediaQueryListEvent) => {
      applySystemTheme(event.matches);
    };

    applySystemTheme(preference.matches);
    preference.addEventListener("change", handleChange);

    return () => {
      preference.removeEventListener("change", handleChange);
    };
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme }), [setTheme, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
